import { ReactNode, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ButtonData } from '../../../../../components/buttonGroup/ButtonGroup';
import { DetailSection, InvoiceLegend, LabelText } from '../../../../../components/generic';
import CurrencyCell from '../../../../../components/tables/cellComponents/CurrencyCell';
import { DateFilterField } from '../../../../../components/tables/filterForms/DateFilterField';
import { FilterField, FilterForm } from '../../../../../components/tables/filterForms/FilterForm';
import { FormSelect } from '../../../../../components/tables/filterForms/FormSelect';
import { SearchField } from '../../../../../components/tables/filterForms/SearchField';
import SFCTable from '../../../../../components/tables/SFCTable';
import { useDataContext, useToast } from '../../../../../context';
import { useListFiltersInUrl } from '../../../../../hooks';
import { useGetAllInvoicesQuery } from '../../../../../services/endpoints/finance/invoice';
import { Scopes, User } from '../../../../../services/endpoints/people/user';
import { useGetVenuesQuery } from '../../../../../services/endpoints/places/venue';
import { getCurrentUser } from '../../../../../services/helper';
import { GetQueryParams } from '../../../../../types/api.type';
import { Invoice } from '../../../../../types/finance.types';
import { invoicesColumns } from '../../../../../utils/consts/columnDefs/financeColumns';
import { userTypes } from '../../../../../utils/consts';
import {Forbidden} from "../../../../../components/error/Forbidden";
import {
  getFilterValue,
  parseInvoicesDeepLinkFilters,
  preserveFilterFields
} from '../../../../../utils/listFilterSearchParams';
import { canSeeFinanceSummary } from '../../../../../utils/rbac';

// RBAC-FE-203: Global Admin excluded from Finance per policy [H]
// Note: Cannot use user?.isAdmin because isAdmin() helper includes global_admin (user_type 3)
// Must explicitly check user_type_id to exclude Global Admin
export const userCanAdmin_Invoices = (user: User | null) =>
    !!(user?.isSuper ||
        user?.isSupremeAdmin ||
        user?.user_type_id === userTypes.admin ||
        user?.isLocationAdmin);

export const InvoicesView = () => {
  const currentUser = getCurrentUser();
  const { addToast } = useToast();
  // Peek legacy `status` deep-link param for initial scope default only; once the hook
  // takes over, scope lives in the URL `scope` param (see parseListFiltersFromSearch).
  const [searchParams] = useSearchParams();
  const statusFromUrl = searchParams.get('status');
  const defaultScope = statusFromUrl || 'open';

  const { locationOptions } = useDataContext();
  const [filters, setFilters] = useListFiltersInUrl(
    {
      scope: defaultScope,
      data: { page: 1, entries: 10, orderBy: { field: 'issue_on', order: 'asc' } }
    },
    { parseExtra: parseInvoicesDeepLinkFilters }
  );
  const [scopes, setScopes] = useState<Scopes>({});
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [buttons, setButtons] = useState<ButtonData[][]>();
  const canAccessInvoices = userCanAdmin_Invoices(currentUser);

  const { data, error, isLoading, isFetching } = useGetAllInvoicesQuery(filters, {
    skip: !canAccessInvoices
  });

  const {
    data: venues,
    error: venuesError
  } = useGetVenuesQuery({ data: { entries: 10000, orderBy: { field: 'title', order: 'asc' } } }, {
    skip: !canAccessInvoices
  });

  const filterFormFields: FilterField[] = [
    {
      name: 'name',
      element: (
          <SearchField
              name="name"
              label="Invoice Name"
              placeholder="Search invoice name"
          />
      ),
      value: getFilterValue(filters.data?.filterBy, 'name')
    },
    {
      name: 'id',
      label: 'ID',
      type: 'number',
      value: getFilterValue(filters.data?.filterBy, 'id')
    },
    ...(currentUser?.isGlobal
      ? [{
          name: 'location_id',
          type: 'number' as const,
          value: (locationOptions.find(
              (option) => String(option.value) === getFilterValue(filters.data?.filterBy, 'location_id')
          ) ?? '') as any,
          element: (
              <FormSelect
                  name="location_id"
                  label="Location"
                  options={locationOptions}
                  selected_Value={locationOptions.find(
                      (option) => String(option.value) === getFilterValue(filters.data?.filterBy, 'location_id')
                  )}
              />
          )
        }]
      : []),
    {
      name: 'venue_id',
      type: 'number',
      value: (venues?.results
          ?.map((venue) => ({ value: venue.id, label: venue.title }))
          .find(
              (option) => String(option.value) === getFilterValue(filters.data?.filterBy, 'venue_id')
          ) ?? '') as any,
      element: (
          <FormSelect
              name="venue_id"
              label="Venue"
              options={venues?.results?.map((venue) => ({ value: venue.id, label: venue.title })) ?? []}
              loadingError={venuesError ? 'Error loading venues' : undefined}
              selected_Value={venues?.results
                  ?.map((venue) => ({ value: venue.id, label: venue.title }))
                  .find((option) => String(option.value) === getFilterValue(filters.data?.filterBy, 'venue_id'))}
          />
      )
    },
    {
      name: 'issue_on_from',
      id: 'issue_on',
      operator: 'greater_equal_than',
      element: (
          <DateFilterField
              name="issue_on_from"
              label="Issue On"
          />
      ),
      value: getFilterValue(filters.data?.filterBy, 'issue_on', 'greater_equal_than')
    },
    {
      name: 'issue_on_to',
      id: 'issue_on',
      operator: 'lesser_equal_than',
      element: <DateFilterField name="issue_on_to" />,
      value: getFilterValue(filters.data?.filterBy, 'issue_on', 'lesser_equal_than')
    },
    {
      name: 'due_on_from',
      id: 'due_on',
      operator: 'greater_equal_than',
      element: (
          <DateFilterField
              name="due_on_from"
              label="Due Date"
          />
      ),
      value: getFilterValue(filters.data?.filterBy, 'due_on', 'greater_equal_than')
    },
    {
      name: 'due_on_to',
      id: 'due_on',
      operator: 'lesser_equal_than',
      element: <DateFilterField name="due_on_to" />,
      value: getFilterValue(filters.data?.filterBy, 'due_on', 'lesser_equal_than')
    },
    {
      name: 'sent_at_from',
      id: 'sent_at',
      operator: 'greater_equal_than',
      element: (
          <DateFilterField
              name="sent_at_from"
              label="Sent At"
          />
      ),
      value: getFilterValue(filters.data?.filterBy, 'sent_at', 'greater_equal_than')
    },
    {
      name: 'sent_at_to',
      id: 'sent_at',
      operator: 'lesser_equal_than',
      element: <DateFilterField name="sent_at_to" />,
      value: getFilterValue(filters.data?.filterBy, 'sent_at', 'lesser_equal_than')
    }
  ];

  const sidebars: ReactNode[] = [<InvoiceLegend />];
  if (canSeeFinanceSummary(currentUser) && ['open', 'draft', 'sent', 'late'].includes(filters.scope ?? '')) {
    sidebars.unshift(
        <DetailSection title="Summary">
          <LabelText label="Total Amount Due:" direction="vertical">
            <CurrencyCell value={String(data?.totals?.amount_due ?? 0)} />
          </LabelText>
        </DetailSection>
    );
  }

  useEffect(() => {
    if (!error && !isLoading && data && data.results) {
      if (data.scopes) {
        setScopes(
            data.scopes.reduce((acc: Scopes, curr: Scopes) => ({ ...acc, ...curr }), {} as Scopes)
        );
        setButtons(
            data.scopes.map((sMap) =>
                Object.entries(sMap).map(([scope, title]) => {
                  let scope_count = 0;
                  if (data.scope_counts != null) {
                    scope_count = data.scope_counts[scope] ?? 0;
                  }
                  return {
                    title: `${title} (${scope_count})`,
                    key: scope
                  };
                })
            )
        );
      }
    } else if (error) {
      addToast('Error while loading data', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, error, isLoading]);

  useEffect(() => {
    if (data && data.results) {
      setInvoices(data.results);
    }
  }, [data]);

  const setFilterBy = (filterBy: { operator: string; field: string; value: string }[]) => {
    setFilters((prev) => ({
      ...prev,
      data: {
        ...prev!.data,
        filterBy: preserveFilterFields(filterBy, prev.data?.filterBy, ['course_id'])
      }
    }));
  };

  // RBAC-FE-203: Defense-in-depth guard for finance pages
  if (!canAccessInvoices) {
    return <Forbidden />;
  }

  return (
      <div className="py-2">
        <SFCTable
            name={`${scopes[filters?.scope!] ?? ''} Invoices`}
            columns={invoicesColumns}
            data={invoices}
            count={data?.count}
            defaultScope={filters.scope ?? defaultScope}
            scopes={buttons}
            isLoading={isLoading}
            isFetching={isFetching}
            indexDownloadPath={'invoices'}
            filters={filters?.data?.filterBy}
            filterForm={
              <FilterForm
                  fields={filterFormFields}
                  onApplyFilters={setFilterBy}
              />
            }
            onFiltersChanged={(newFilters) => {
              // course_id is a deep-link-only filter (not in FilterForm), so it must be
              // preserved explicitly whenever SFCTable pushes scope/pagination/sort changes.
              setFilters((prev) => {
                const resolved: GetQueryParams =
                    typeof newFilters === 'function' ? newFilters(prev) : newFilters;
                return {
                  ...resolved,
                  data: {
                    ...resolved.data,
                    filterBy: preserveFilterFields(resolved.data?.filterBy ?? [], prev.data?.filterBy, [
                      'course_id'
                    ])
                  }
                };
              });
            }}
            sidebars={sidebars}
        />
      </div>
  );
};